import { useEffect, useState } from 'react';
import {
  Chip,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { DeleteRounded as DeleteIcon, DragIndicator as DragIndicatorIcon } from '@material-ui/icons';

import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { Basket } from '../../types/basket.type';
import { BasketProduct } from '../../types/basket-product.type';
import { Product } from '../../../products/types/product.type';
import { useUpdateBasket } from '../../hooks/update-basket.hook';
import { CatalogProductsList } from '../../../shared/components/Catalog/CatalogProductsList.component';
import { BASKET_IMAGE_PATH } from '../../constants/baskets-app-paths.constants';

type Props = {
  basket: Basket;
  refreshBasket: () => void;
};

const LAST_PRODUCT_TOOLTIP = 'Un panier contient au moins un produit';

const useStyles = makeStyles((theme) => ({
  root: {
    overflow: 'auto',

    '& h4': {
      margin: '0 0 0.5rem 0',
      opacity: 0.7,
    },

    '& .MuiOutlinedInput-root.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.admin.primary.main,
    },
    '& .MuiInputLabel-outlined.Mui-focused:not(.Mui-error)': {
      color: theme.palette.admin.primary.main,
    },
  },
  headerCell: {
    fontWeight: 'bold',
  },
  secondaryLine: {
    display: 'block',
    fontSize: 12,
    color: '#757575',
  },
  compositionContainer: {
    maxHeight: '55svh',
    overflow: 'auto',
  },
  drag: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.4rem',
  },
  positionChip: {
    backgroundColor: theme.palette.admin.primary.main,
    color: theme.palette.admin.primary.contrastText,
    fontWeight: 600,
    fontSize: 12,
    borderRadius: 4,
  },
  productImage: {
    borderRadius: 4,
    objectFit: 'cover',
  },
  emptyCell: {
    color: '#757575',
    textAlign: 'center',
  },
  panelContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    padding: '1rem 0.75rem',
  },
}));

function SortableBasketProductRow({
  basketProduct,
  position,
  isLastProduct,
  onRemove,
}: {
  basketProduct: BasketProduct;
  position: number;
  isLastProduct: boolean;
  onRemove: (productId: string) => void;
}) {
  const classes = useStyles();
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: basketProduct.productId,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <TableRow ref={setNodeRef} style={style} hover tabIndex={-1}>
      <TableCell align="left">
        <div className={classes.drag}>
          <DragIndicatorIcon {...attributes} {...listeners} style={{ cursor: 'grab', opacity: 0.6 }} />
          <Chip className={classes.positionChip} size="small" label={position} />
        </div>
      </TableCell>
      <TableCell align="center">
        <img
          className={classes.productImage}
          src={BASKET_IMAGE_PATH(basketProduct.photo)}
          alt={basketProduct.name}
          width={40}
          height={40}
        />
      </TableCell>
      <TableCell align="left">
        {basketProduct.name}
        <span className={classes.secondaryLine}>Réf. {basketProduct.reference}</span>
      </TableCell>
      <TableCell align="left">
        {basketProduct.weight.quantity} {basketProduct.weight.abbreviation}
      </TableCell>
      <TableCell align="left">{basketProduct.origin || '—'}</TableCell>
      <TableCell align="right">
        <Tooltip title={isLastProduct ? LAST_PRODUCT_TOOLTIP : 'Retirer du panier'}>
          <span>
            <IconButton
              size="small"
              aria-label="Retirer du panier"
              disabled={isLastProduct}
              onClick={() => onRemove(basketProduct.productId)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
}

export default function BasketProductsTab({ basket, refreshBasket }: Props) {
  const classes = useStyles();
  const { updateBasket, isSubmitting } = useUpdateBasket();

  const [orderedProducts, setOrderedProducts] = useState<BasketProduct[]>(basket.products);

  useEffect(() => {
    setOrderedProducts(basket.products);
  }, [basket.products]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const basketProductIds = orderedProducts.map((basketProduct) => basketProduct.productId);

  const persistComposition = async (productIds: string[]) => {
    if (isSubmitting) return;

    const updatedBasket = await updateBasket({
      basketId: basket.id,
      basketData: { products: productIds.map((productId) => ({ productId })) },
    });
    if (!updatedBasket) return;

    setOrderedProducts(updatedBasket.products);
    refreshBasket();
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    if (isSubmitting) return;

    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = orderedProducts.findIndex((basketProduct) => basketProduct.productId === active.id);
    const newIndex = orderedProducts.findIndex((basketProduct) => basketProduct.productId === over.id);
    const previousOrder = orderedProducts;
    const reordered = arrayMove(orderedProducts, oldIndex, newIndex);

    setOrderedProducts(reordered);
    const updated = await updateBasket({
      basketId: basket.id,
      basketData: { products: reordered.map((basketProduct) => ({ productId: basketProduct.productId })) },
    });
    if (!updated) setOrderedProducts(previousOrder);
  };

  const handleAddProduct = async (product: Product) => {
    await persistComposition([...basketProductIds, product.id]);
  };

  const handleRemoveProduct = async (productId: string) => {
    if (basketProductIds.length <= 1) return;

    await persistComposition(basketProductIds.filter((basketProductId) => basketProductId !== productId));
  };

  return (
    <section className={classes.root}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <h4>Composition du panier ({orderedProducts.length})</h4>
          <Paper>
            <TableContainer className={classes.compositionContainer}>
              <Table size="small" stickyHeader aria-label="Produits du panier">
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.headerCell} style={{ width: 90 }} />
                    <TableCell className={classes.headerCell} style={{ width: 60 }} />
                    <TableCell className={classes.headerCell}>Produit</TableCell>
                    <TableCell className={classes.headerCell}>Poids</TableCell>
                    <TableCell className={classes.headerCell}>Origine</TableCell>
                    <TableCell
                      className={classes.headerCell}
                      align="right"
                      style={{ width: 60 }}
                      aria-label="Actions"
                    />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orderedProducts.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className={classes.emptyCell}>
                        Ce panier est vide : ajoutez des produits depuis le panneau de droite.
                      </TableCell>
                    </TableRow>
                  )}
                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext
                      items={orderedProducts.map((basketProduct) => basketProduct.productId)}
                      strategy={verticalListSortingStrategy}
                    >
                      {orderedProducts.map((basketProduct, productIndex) => (
                        <SortableBasketProductRow
                          key={basketProduct.productId}
                          basketProduct={basketProduct}
                          position={productIndex + 1}
                          isLastProduct={orderedProducts.length === 1}
                          onRemove={handleRemoveProduct}
                        />
                      ))}
                    </SortableContext>
                  </DndContext>
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <h4>Ajouter des produits</h4>
          <Paper className={classes.panelContent}>
            <CatalogProductsList
              onAdd={handleAddProduct}
              excludedProductIds={basketProductIds}
              disabled={isSubmitting}
            />
          </Paper>
        </Grid>
      </Grid>
    </section>
  );
}
